import React, { useState, useEffect } from 'react'
import {
  Zoning,
  ZoningType,
  ZoneDocumentsGroupedResponse,
  LandUsePlanDocument,
  DocumentSource,
} from '@popety-io/types'
import { Icon } from '../../../../atoms/Icon'
import { DocumentsList } from './components/DocumentsList'
import { DocumentChips } from './components/DocumentChips'
import { DocumentViewerModal } from './DocumentViewerModal'
import { useDocumentModal } from './hooks/useDocumentModal'
import {
  SectionContainer,
  SectionHeader,
  HeaderTitle,
  LoadingState,
} from './DocumentsSection.styles'

interface ZoningServiceType {
  getZoneDocuments: (
    type: ZoningType,
    id: string
  ) => Promise<ZoneDocumentsGroupedResponse>
  /** Optional: transform a PDF URL through a proxy to bypass CORS */
  getPdfProxyUrl?: (url: string) => string
}

export interface DocumentsSectionTranslations {
  title?: string
  loading?: string
  noDocuments?: string
  view?: string
  modalTitle?: string
  searchPlaceholder?: string
  searchAriaLabel?: string
  clearSearchAriaLabel?: string
  noDocumentSelected?: string
  selectDocumentHint?: string
  important?: string
  noPreviewAvailable?: string
  noPreviewHint?: string
  documentTypes?: {
    rule?: string
    map?: string
  }
  sourceLabels?: {
    lupa?: string
    lupc?: string
    lupo?: string
    lup?: string
    region?: string
    country?: string
  }
  sourceTitles?: {
    lupa?: string
    lupc?: string
    lupo?: string
    lup?: string
    region?: string
    country?: string
  }
}

const DEFAULT_DOCUMENTS_TRANSLATIONS: DocumentsSectionTranslations = {
  title: 'Documents',
  loading: 'Loading documents...',
  noDocuments: 'No documents available',
  view: 'View',
  modalTitle: 'Zone Documents',
  searchPlaceholder: 'Search documents...',
  searchAriaLabel: 'Search documents',
  clearSearchAriaLabel: 'Clear search',
  noDocumentSelected: 'No Document Selected',
  selectDocumentHint: 'Select a document from the list to view its contents',
  important: 'Important',
  noPreviewAvailable: 'No Preview Available',
  noPreviewHint: 'This document does not have a PDF preview URL available',
  documentTypes: {
    rule: 'Rule',
    map: 'Map',
  },
  sourceLabels: {
    lupa: 'Local Plan',
    lupc: 'Cantonal Plan',
    lupo: 'Overlay Plan',
    lup: 'Communal Plan',
    region: 'Cantonal',
    country: 'Federal',
  },
  sourceTitles: {
    lupa: 'Local Plan Documents',
    lupc: 'Cantonal Plan Documents',
    lupo: 'Overlay Plan Documents',
    lup: 'Communal Plan Documents',
    region: 'Cantonal Documents',
    country: 'Federal Documents (Switzerland)',
  },
}

interface DocumentsSectionProps {
  zone: Zoning
  zoningType: ZoningType
  zoningService: ZoningServiceType
  translations?: DocumentsSectionTranslations
  /** Optional locale for date formatting (e.g., 'en', 'fr', 'de', 'it') */
  locale?: string
  /** Hide the section header (useful when rendered inside a tab) */
  hideTitle?: boolean
  /** Callback reporting total document count after fetch */
  onDocumentCount?: (count: number) => void
  /** Zone context for breadcrumb header in document modal */
  municipalityName?: string
  planName?: string
  zoneName?: string
}

export const DocumentsSection: React.FC<DocumentsSectionProps> = ({
  zone,
  zoningType,
  zoningService,
  translations,
  locale,
  hideTitle,
  onDocumentCount,
  municipalityName,
  planName,
  zoneName,
}) => {
  // Merge translations with defaults
  const t = {
    ...DEFAULT_DOCUMENTS_TRANSLATIONS,
    ...translations,
    documentTypes: {
      ...DEFAULT_DOCUMENTS_TRANSLATIONS.documentTypes,
      ...translations?.documentTypes,
    },
    sourceLabels: {
      ...DEFAULT_DOCUMENTS_TRANSLATIONS.sourceLabels,
      ...translations?.sourceLabels,
    },
    sourceTitles: {
      ...DEFAULT_DOCUMENTS_TRANSLATIONS.sourceTitles,
      ...translations?.sourceTitles,
    },
  }
  const [previewDocuments, setPreviewDocuments] = useState<
    LandUsePlanDocument[]
  >([])
  const [documentsBySource, setDocumentsBySource] = useState<
    Partial<Record<DocumentSource, LandUsePlanDocument[]>>
  >({})
  const [precedenceOrder, setPrecedenceOrder] = useState<DocumentSource[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [initialSource, setInitialSource] = useState<DocumentSource>('lupa')
  const [, setPreselectedDoc] = useState<LandUsePlanDocument | null>(null)
  const { isOpen, selectedDocument, openModal, closeModal, selectDocument } =
    useDocumentModal()

  // Fetch grouped documents
  useEffect(() => {
    const fetchDocuments = async () => {
      const zoneId = zone._id
      if (!zoneId) {
        setIsLoading(false)
        return
      }

      try {
        setIsLoading(true)

        const response = await zoningService.getZoneDocuments(
          zoningType,
          zoneId
        )

        // Store grouped documents
        setDocumentsBySource(response.documents_by_source)
        setPrecedenceOrder(response.precedence_order)
        setTotalCount(response.total)
        onDocumentCount?.(response.total)

        // Get first 2 documents from highest precedence source for preview
        const firstSource = response.precedence_order[0]
        const firstSourceDocs = firstSource
          ? response.documents_by_source[firstSource] || []
          : []
        setPreviewDocuments(firstSourceDocs.slice(0, 2))
      } catch (error) {
        console.error('Error fetching documents:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchDocuments()
  }, [zone._id, zoningType, zoningService])

  // Handle document view from list (opens modal with PDF viewer)
  const handleViewDocument = (doc: LandUsePlanDocument) => {
    setPreselectedDoc(doc)
    // Set initial source based on which source this document belongs to
    const docSource =
      precedenceOrder.find((src) =>
        documentsBySource[src]?.some((d) => d.id === doc.id)
      ) || 'lupa'
    setInitialSource(docSource)
    selectDocument(doc) // Set in modal state
    openModal()
  }

  // Handle chip click (opens modal in browse mode)
  const handleChipClick = (source: DocumentSource) => {
    setPreselectedDoc(null)
    setInitialSource(source)
    selectDocument(null) // Clear selection to show browse mode
    openModal()
  }

  return (
    <>
      <SectionContainer>
        {!hideTitle && (
          <SectionHeader>
            <Icon name="file-text" size="small" />
            <HeaderTitle>{t.title}</HeaderTitle>
          </SectionHeader>
        )}

        {isLoading ? (
          <LoadingState>{t.loading}</LoadingState>
        ) : (
          <>
            <DocumentsList
              documents={previewDocuments}
              onViewDocument={handleViewDocument}
              translations={{
                noDocuments: t.noDocuments,
                view: t.view,
              }}
            />

            <DocumentChips
              documentsBySource={documentsBySource}
              precedenceOrder={precedenceOrder}
              totalCount={totalCount}
              onChipClick={handleChipClick}
              translations={{
                sourceLabels: t.sourceLabels,
              }}
            />
          </>
        )}
      </SectionContainer>

      <DocumentViewerModal
        isOpen={isOpen}
        onClose={closeModal}
        selectedDocument={selectedDocument}
        initialSource={initialSource}
        documentsBySource={documentsBySource}
        precedenceOrder={precedenceOrder}
        onSelectDocument={selectDocument}
        zoningService={zoningService}
        municipalityName={municipalityName}
        planName={planName}
        zoneName={zoneName}
        getPdfProxyUrl={zoningService.getPdfProxyUrl}
        translations={{
          modalTitle: t.modalTitle,
          searchPlaceholder: t.searchPlaceholder,
          searchAriaLabel: t.searchAriaLabel,
          clearSearchAriaLabel: t.clearSearchAriaLabel,
          noDocumentSelected: t.noDocumentSelected,
          selectDocumentHint: t.selectDocumentHint,
          important: t.important,
          noPreviewAvailable: t.noPreviewAvailable,
          noPreviewHint: t.noPreviewHint,
          noDocuments: t.noDocuments,
          sourceTitles: t.sourceTitles,
          documentTypes: t.documentTypes,
        }}
        locale={locale}
      />
    </>
  )
}
